import type Button from '@naturehouse/design-system/components/atoms/button/Button';

const RESEND_EMAIL_VERIFY_BUTTON_ROLE = 'resend-email-verify-button';
const VERIFY_BUTTON_SELECTOR = `[data-role="${RESEND_EMAIL_VERIFY_BUTTON_ROLE}"]`;
const BOUND_ATTRIBUTE = 'data-account-verification-bound';
const ORIGINAL_LABEL_ATTRIBUTE = 'data-account-verification-original-label';

export type AccountVerificationButtonState = {
    disabled: boolean;
    onCooldown: boolean;
    remainingSeconds: number;
};

type VerifyButtonClickHandler = (action: string, button: HTMLElement) => Promise<void>;

export default class AccountVerificationButtonManager {
    public bindButtons(onClick: VerifyButtonClickHandler): void {
        document.querySelectorAll<HTMLElement>(VERIFY_BUTTON_SELECTOR).forEach((button) => {
            if (button.hasAttribute(BOUND_ATTRIBUTE)) {
                return;
            }

            button.setAttribute(BOUND_ATTRIBUTE, 'true');
            button.addEventListener('click', async () => {
                const action = button.getAttribute('data-action');

                if (!action) {
                    return;
                }

                await onClick(action, button);
            });
        });
    }

    public applyState({
        disabled,
        onCooldown,
        remainingSeconds
    }: AccountVerificationButtonState): void {
        document.querySelectorAll<HTMLElement>(VERIFY_BUTTON_SELECTOR).forEach((element) => {
            if ('disabled' in element) {
                (element as Button).disabled = disabled;
            }

            this.#applyLabelState(element, disabled, onCooldown, remainingSeconds);
        });
    }

    #applyLabelState(
        element: HTMLElement,
        disabled: boolean,
        onCooldown: boolean,
        remainingSeconds: number
    ): void {
        if (disabled && onCooldown) {
            this.#updateCountdownLabel(element, remainingSeconds);
            return;
        }

        if (!onCooldown) {
            this.#restoreOriginalLabel(element);
        }
    }

    #getButtonLabelElement(button: HTMLElement): HTMLElement | null {
        return button.querySelector('.nh-button__label');
    }

    #storeOriginalLabel(button: HTMLElement): void {
        if (button.hasAttribute(ORIGINAL_LABEL_ATTRIBUTE)) {
            return;
        }

        const labelElement = this.#getButtonLabelElement(button);
        const label = labelElement?.textContent?.trim() ?? '';

        if (label !== '') {
            button.setAttribute(ORIGINAL_LABEL_ATTRIBUTE, label);
        }
    }

    #restoreOriginalLabel(button: HTMLElement): void {
        const originalLabel = button.getAttribute(ORIGINAL_LABEL_ATTRIBUTE);

        if (originalLabel === null) {
            return;
        }

        const labelElement = this.#getButtonLabelElement(button);

        if (labelElement !== null) {
            labelElement.textContent = originalLabel;
        }

        button.removeAttribute(ORIGINAL_LABEL_ATTRIBUTE);
    }

    #updateCountdownLabel(button: HTMLElement, remainingSeconds: number): void {
        this.#storeOriginalLabel(button);

        const originalLabel = button.getAttribute(ORIGINAL_LABEL_ATTRIBUTE);

        if (originalLabel === null) {
            return;
        }

        const labelElement = this.#getButtonLabelElement(button);

        if (labelElement === null) {
            return;
        }

        labelElement.textContent =
            remainingSeconds > 0 ? `${originalLabel} (${remainingSeconds}s)` : originalLabel;
    }
}
